import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect, NavLink as RRNavLink } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import GoogleTranslate from "../components/GoogleTranslate";

import CurrencySelector from "../components/CurrencySelector";
import CommandPalette from "../components/CommandPalette";
import Breadcrumbs from "../components/Breadcrumbs";
import EnhancedTooltip from "../components/EnhancedTooltip";
import DarkModeToggle from "../components/DarkModeToggle";
import { ToastProvider } from "../components/ToastProvider";
import { authLogout } from "../actions/auth";
import { fetchOpenTicketsCount } from "../actions/supporttickets";
import { fetchOpenIncidencesCount } from "../actions/incidences";
import { canAccess, hasAccessToAny } from "../utils/permissions";

// lucide-react icons
import {
  LayoutGrid,
  Coins,
  Menu as MenuIcon,
  LogOut,
  User as UserIcon,
  Monitor,
  Filter,
  Star,
  BarChart2,
  PieChart,
  Calculator,
  Target,
  Crown,
  FileSignature,
  Gamepad2,
  SlidersHorizontal,
  UserCog,
  ArrowRight,
  ArrowLeft,
  Clock,
  Users,
  Database,
  Download,
  Smartphone,
  Info,
  Search,
  TrendingUp,
  Boxes,
  Calendar,
  HelpCircle,
  BookOpen,
  MessageSquare,
  RefreshCw,
  Newspaper,
  ChevronDown,
  Headset,
  ShieldCheck,
  Receipt,
  Ticket,
  AlertTriangle,
  NotebookTabs,
  Briefcase,
  Gauge,
  IdCard,
} from "lucide-react";

/**
 * Inject styles once on mount
 */
const injectStyles = () => {
  if (document.getElementById("app-shell-styles")) return;
  const css = `
/* ==================== VARIABLES ==================== */
/* Light = the VACWAY Ecosystem shell (tech look, copied from its AppShell); dark = the same chrome in VACWAY blues
   (DARK MODE block below). Only the chrome goes dark: the page content stays light in both modes. */
:root {
  --vw-dark: #0f172a;
  --vw-dark-900: #0b1223;
  --vw-dark-gradient: linear-gradient(to bottom, #0f172a, #01315f);
  --vw-accent: #1e293b;
  --sidebar-w: 280px;
  --sidebar-collapsed-w: 72px;
  --sidebar-icons-w: 72px;
  --sidebar-sub-w: 260px;
  --topbar-h: 64px;
  --mobile-bottom-nav-h: 70px;
  --danger: #ef4444;
  --focus-ring: 0 0 0 3px rgba(99, 102, 241, 0.45);
  --focus-ring-light: 0 0 0 3px rgba(255, 255, 255, 0.3);

  --bg-primary: #f5f7fb;
  --bg-secondary: #ffffff;
  --text-primary: #0f172a;
  --text-secondary: #64748b;
  --border-color: #e5e7eb;

  /* Tech palette (cyan → indigo → violet) */
  --tx-cyan: #06b6d4;
  --tx-indigo: #6366f1;
  --tx-violet: #8b5cf6;
  --tx-grad: linear-gradient(135deg, #06b6d4 0%, #6366f1 55%, #8b5cf6 100%);
  --tx-grad-soft: linear-gradient(135deg, rgba(6, 182, 212, 0.10), rgba(99, 102, 241, 0.12) 55%, rgba(139, 92, 246, 0.10));
  --tx-line: linear-gradient(90deg, transparent 0%, #06b6d4 22%, #6366f1 50%, #8b5cf6 78%, transparent 100%);
  --tx-glass: rgba(255, 255, 255, 0.78);
  --tx-hair: rgba(15, 23, 42, 0.08);
  --tx-hair-strong: rgba(99, 102, 241, 0.22);
  --tx-glow: 0 0 0 1px rgba(99, 102, 241, 0.25), 0 8px 22px -8px rgba(99, 102, 241, 0.55);
  --tx-mono: ui-monospace, "SFMono-Regular", "JetBrains Mono", "Cascadia Code", Menlo, Consolas, monospace;

  /* Topbar */
  --topbar-bg: rgba(255, 255, 255, 0.92);
  --topbar-text: #0f172a;
  --topbar-border: var(--tx-hair);
  --topbar-btn-border: rgba(15, 23, 42, 0.10);
  --topbar-btn-hover: rgba(99, 102, 241, 0.07);
  --topbar-icon-color: #64748b;

  /* Sidebar */
  --sidebar-bg: #ffffff;
  --sidebar-icons-bg: #fbfcff;
  --sidebar-border: var(--tx-hair);
  --sidebar-text: #0f172a;
  --sidebar-text-secondary: #475569;
  --sidebar-icon-color: #64748b;
  --sidebar-icon-active: #4f46e5;
  --sidebar-btn-hover: rgba(99, 102, 241, 0.07);
  --sidebar-btn-active: rgba(99, 102, 241, 0.10);
  --sidebar-submenu-bg: #ffffff;
  --sidebar-submenu-label: #6366f1;
  --sidebar-submenu-border: var(--tx-hair);
}

/* ==================== TOPBAR ==================== */
.topbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: var(--topbar-h);
  background: var(--tx-glass);
  -webkit-backdrop-filter: blur(16px) saturate(170%);
  backdrop-filter: blur(16px) saturate(170%);
  color: var(--topbar-text);
  display: flex;
  align-items: center;
  z-index: 1030;
  border-bottom: 1px solid var(--tx-hair);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 10px 30px -22px rgba(15, 23, 42, 0.45);
}

/* Energy line along the bottom edge */
.topbar::after {
  content: "";
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 2px;
  background: var(--tx-line);
  background-size: 200% 100%;
  opacity: 0.85;
  pointer-events: none;
  animation: tx-flow 9s linear infinite;
}
@keyframes tx-flow {
  from { background-position: 0% 0; }
  to   { background-position: 200% 0; }
}

.topbar__inner {
  width: 100%;
  padding: 0 18px 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.topbar__left, .topbar__right {
  display: flex;
  align-items: center;
}

.topbar__brand {
  display: flex;
  align-items: center;
  padding: 4px 6px;
  border-radius: 10px;
}
.topbar__brand img {
  height: 28px;
  display: block;
}
.topbar__brand:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* Blue logo on the light chrome, white logo on the dark (VACWAY blue) chrome */
.topbar__brand .logo-dark { display: none; }
.dark-mode .topbar__brand .logo-dark { display: block; }
.dark-mode .topbar__brand .logo-light { display: none; }

.topbar__sep {
  width: 1px;
  height: 28px;
  margin: 0 14px 0 8px;
  background: linear-gradient(to bottom, transparent, rgba(99, 102, 241, 0.45), transparent);
}

/* ── platform switcher: HUD chip + glass panel ── */
.psw-chip {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 34px;
  padding: 0 12px 0 11px;
  background: linear-gradient(#ffffff, #ffffff) padding-box, var(--tx-grad) border-box;
  border: 1px solid transparent;
  border-radius: 999px;
  color: #0f172a;
  font-family: var(--tx-mono);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.2em;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 6px 16px -10px rgba(99, 102, 241, 0.7);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.psw-chip:hover { box-shadow: var(--tx-glow); transform: translateY(-1px); }
.psw-chip:active { transform: translateY(0); }
.psw-chip:focus { outline: none; }
.psw-chip:focus-visible { box-shadow: var(--focus-ring); }
.psw-chip-label {
  background: var(--tx-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.psw-chip-dot {
  position: relative;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.16), 0 0 10px rgba(16, 185, 129, 0.7);
  flex-shrink: 0;
}
.psw-chip-dot::after {
  content: "";
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1px solid rgba(16, 185, 129, 0.6);
  animation: tx-ping 2.6s ease-out infinite;
}
@keyframes tx-ping {
  0%       { transform: scale(0.5); opacity: 0.9; }
  80%,100% { transform: scale(1.8); opacity: 0; }
}
.psw-chip-chevron {
  color: #6366f1;
  transition: transform 0.2s;
}
.psw-chip-chevron--open { transform: rotate(180deg); }
.psw-chip-backdrop {
  position: fixed; inset: 0; z-index: 1998;
}
.psw-chip-panel {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 1999;
  min-width: 250px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: rgba(255, 255, 255, 0.94);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  backdrop-filter: blur(18px) saturate(170%);
  border: 1px solid var(--tx-hair-strong);
  border-radius: 14px;
  box-shadow: 0 28px 56px -24px rgba(15, 23, 42, 0.4), 0 0 0 4px rgba(99, 102, 241, 0.05);
  animation: psw-fadein 0.16s ease;
}
.psw-chip-panel::before {
  content: "PLATAFORMAS";
  font-family: var(--tx-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: #94a3b8;
  padding: 6px 12px 8px;
}
@keyframes psw-fadein {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.psw-chip-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none !important;
  color: #0f172a !important;
  font-family: var(--tx-mono);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  transition: background 0.15s ease, transform 0.15s ease;
}
.psw-chip-card:hover { background: rgba(99, 102, 241, 0.07); transform: translateX(3px); }
.psw-chip-card::after {
  content: "→";
  color: #94a3b8;
  transition: transform 0.15s ease, color 0.15s ease;
}
.psw-chip-card:hover::after { transform: translateX(3px); color: #6366f1; }
/* Dot coloured inline (background + color = platform colour) so the glow uses it too */
.psw-chip-card-accent {
  width: 8px; height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 10px currentColor;
}
.psw-chip-card-label { flex: 1; }

/* ── glass keys (user, logout and the other topbar buttons) ── */
.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--topbar-btn-border);
  border-radius: 10px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, background 0.15s ease;
}
.btn-ghost:hover {
  background: #ffffff;
  border-color: var(--tx-hair-strong);
  box-shadow: var(--tx-glow);
  transform: translateY(-1px);
}
.btn-ghost:active { transform: translateY(0); }
.btn-ghost:focus { outline: none; box-shadow: var(--focus-ring); }

.logout-btn { color: #475569; }
.logout-btn:hover {
  color: #e11d48;
  border-color: rgba(225, 29, 72, 0.35);
  box-shadow: 0 0 0 1px rgba(225, 29, 72, 0.18), 0 8px 20px -10px rgba(225, 29, 72, 0.6);
}

/* Avatar with gradient ring inside the user key */
.user-avatar {
  width: 24px; height: 24px;
  padding: 1.5px;
  border-radius: 50%;
  background: var(--tx-grad);
  display: inline-flex;
  flex-shrink: 0;
}
.user-avatar > img,
.user-avatar > span {
  width: 100%; height: 100%;
  border-radius: 50%;
  background: #ffffff;
  object-fit: cover;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
}

/* Mono clock readout */
.tx-clock {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  margin-right: 16px;
  padding-right: 16px;
  border-right: 1px solid var(--tx-hair);
  font-family: var(--tx-mono);
  line-height: 1.1;
  user-select: none;
}
.tx-clock-time {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #0f172a;
}
.tx-clock-date {
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #94a3b8;
}
@media (max-width: 1100px) {
  .tx-clock { display: none; }
}

.nightscan-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.nightscan-btn:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.nightscan-btn:active {
  transform: translateY(0);
}
.nightscan-btn:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nightscan-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--danger);
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

/* Tickets indicator button */
.tickets-indicator-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tickets-indicator-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
  transform: translateY(-1px);
}
.tickets-indicator-btn:active {
  transform: translateY(0);
}
.tickets-indicator-btn .indicator-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #3b82f6;
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

/* Incidences indicator button */
.incidences-indicator-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.incidences-indicator-btn:hover {
  background: rgba(249, 115, 22, 0.1);
  border-color: #f97316;
  transform: translateY(-1px);
}
.incidences-indicator-btn:active {
  transform: translateY(0);
}
.incidences-indicator-btn .indicator-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f97316;
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

.dropdown-toggle:after {
  color: var(--topbar-text) !important;
}

/* Language dropdown styling */
.nav-item.dropdown {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
}

.dropdown-toggle {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
}

.dropdown-toggle::after {
  margin-left: 4px !important;
}

/* Mobile-specific topbar */
.topbar__mobileToggle { display: none; }
@media (max-width: 768px) {
  .topbar__right .btn-ghost.user-btn,
  .topbar__right .btn-ghost.logout-btn { display: none; }
  /* Hide nightscan, tickets, incidences buttons on mobile - they're in Profile menu */
  .nightscan-btn,
  .tickets-indicator-btn,
  .incidences-indicator-btn { display: none !important; }
  .topbar__mobileToggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 1px solid var(--topbar-btn-border);
    color: var(--topbar-text);
    padding: 6px 10px;
    border-radius: 10px;
    margin-left: 8px;
  }
  .topbar__mobileToggle:hover { background: var(--topbar-btn-hover); }
}

/* Hide list markers in dropdowns */
.dropdown-menu li::marker,
.nav-item::marker {
  display: none;
  content: none;
}

/* Ensure dropdowns appear above other elements */
.dropdown-menu {
  z-index: 1050;
}

.nav-item {
  list-style: none;
}

/* ==================== LAYOUT ==================== */
.shell {
  display: flex;
  min-height: 100vh;
  background:
    radial-gradient(1100px 420px at 0% 0%, rgba(99, 102, 241, 0.06), transparent 60%),
    radial-gradient(900px 380px at 100% 0%, rgba(6, 182, 212, 0.05), transparent 60%),
    #f5f7fb;
}

/* ==================== SIDEBAR PRO ==================== */
.sidebar-pro {
  position: fixed;
  top: var(--topbar-h);
  left: 0;
  bottom: 0;
  display: flex;
  background: transparent;
  color: var(--sidebar-text);
  z-index: 1020;
  transition: all .25s ease;
}

/* Left rail: dot grid, icon tiles */
.sidebar-icons {
  position: relative;
  width: var(--sidebar-icons-w);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 14px 0 12px;
  background:
    radial-gradient(rgba(99, 102, 241, 0.13) 1px, transparent 1.2px) 0 0 / 14px 14px,
    linear-gradient(180deg, #ffffff 0%, #f5f7ff 100%);
  border-right: 1px solid var(--tx-hair);
  box-shadow: 8px 0 26px -22px rgba(15, 23, 42, 0.45);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.sidebar-icons:hover { scrollbar-color: rgba(99, 102, 241, 0.3) transparent; }
.sidebar-icons::-webkit-scrollbar { width: 4px; }
.sidebar-icons::-webkit-scrollbar-track { background: transparent; }
.sidebar-icons::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 999px;
  transition: background .2s;
}
.sidebar-icons:hover::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.3); }
.sidebar-icons::-webkit-scrollbar-thumb:hover { background: rgba(99, 102, 241, 0.55); }

.icon-btn {
  position: relative;
  width: 64px;
  min-height: 64px;
  padding: 6px 0 7px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: transparent;
  border: none;
  border-radius: 14px;
  color: var(--sidebar-icon-color);
  cursor: pointer;
  outline: none;
  text-decoration: none;
  transition: color .18s ease;
}
.icon-tile {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  transition: background .2s ease, border-color .2s ease, box-shadow .2s ease, color .2s ease, transform .2s ease;
}
.icon-btn:hover { color: #4f46e5; }
.icon-btn:hover .icon-tile {
  background: rgba(99, 102, 241, 0.08);
  border-color: rgba(99, 102, 241, 0.2);
  transform: translateY(-1px);
}
.icon-btn.active { color: #4338ca; }
.icon-btn.active .icon-tile {
  background: var(--tx-grad);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 10px 20px -10px rgba(99, 102, 241, 0.85), 0 0 0 3px rgba(99, 102, 241, 0.10);
}
/* Glowing indicator on the rail edge */
.icon-btn.active::before {
  content: "";
  position: absolute;
  left: -4px;
  top: 14px;
  width: 3px;
  height: 26px;
  border-radius: 0 3px 3px 0;
  background: var(--tx-grad);
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.85);
}
.icon-btn:focus { outline: none; }
.icon-btn:focus-visible .icon-tile { box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px rgba(99, 102, 241, 0.6); }

.icon-label {
  width: 100%;
  padding: 0 2px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.15;
  text-align: center;
  color: inherit;
}

/* Right submenu panel (glass) */
.submenu-panel {
  width: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  backdrop-filter: blur(18px) saturate(170%);
  border-right: 1px solid var(--tx-hair);
  box-shadow: 22px 0 44px -30px rgba(15, 23, 42, 0.5);
  transition: width .2s cubic-bezier(.2, .8, .2, 1);
}
.submenu-panel.open { width: var(--sidebar-sub-w); }

.submenu-content {
  width: var(--sidebar-sub-w);
  height: 100%;
  padding: 18px 14px;
  overflow-y: auto;
  opacity: 0;
  transform: translateX(-10px);
  transition: opacity .2s ease-out, transform .2s cubic-bezier(.2, .8, .2, 1);
}
.submenu-panel.open .submenu-content {
  opacity: 1;
  transform: translateX(0);
}

.submenu-label {
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 8px;
  padding: 4px 10px 12px;
  border-bottom: 1px solid var(--tx-hair);
  font-family: var(--tx-mono);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #6366f1;
}
.submenu-label::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 2px;
  background: var(--tx-grad);
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.75);
}
.submenu-label::after {
  content: "";
  position: absolute;
  left: 10px; bottom: -1px;
  width: 46px; height: 2px;
  border-radius: 2px;
  background: var(--tx-grad);
}

/* Sub-heading of a group inside a submenu (Comercial › Formación / Ventas…): the Ecosystem section-title look */
.submenu-sublabel {
  margin: 12px 0 4px;
  padding: 0 12px;
  font-family: var(--tx-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #94a3b8;
}
.submenu-label + .submenu-sublabel { margin-top: 2px; }

.submenu-content .nav-button {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 4px;
  padding: 9px 12px;
  border: 1px solid transparent;
  border-radius: 11px;
  background: transparent;
  color: #334155;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background .15s ease, border-color .15s ease, color .15s ease, transform .15s ease;
}
.submenu-content .nav-button svg {
  width: 18px; height: 18px;
  min-width: 18px;
  color: #64748b;
  transition: color .15s ease;
}
.submenu-content .nav-button:hover {
  background: rgba(99, 102, 241, 0.06);
  border-color: rgba(99, 102, 241, 0.14);
  color: #0f172a;
  transform: translateX(3px);
}
.submenu-content .nav-button:hover svg { color: #6366f1; }
.submenu-content .nav-button.active {
  background: var(--tx-grad-soft);
  border-color: rgba(99, 102, 241, 0.24);
  color: #312e81;
  font-weight: 600;
}
.submenu-content .nav-button.active svg { color: #4f46e5; }
.submenu-content .nav-button.active::before {
  content: "";
  position: absolute;
  left: -1px; top: 8px; bottom: 8px;
  width: 3px;
  border-radius: 3px;
  background: var(--tx-grad);
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.75);
}
.submenu-content .nav-button:active { transform: translateX(2px); }
.submenu-content .nav-button:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}


/* Red counters: on a rail tile (sum of its submenu counters) and on a submenu entry */
.rail-badge { position: absolute; top: 2px; left: calc(50% + 9px); min-width: 16px; height: 16px; padding: 0 4px; border-radius: 999px; background: #ef4444; color: #fff; font-size: 10px; font-weight: 700; line-height: 1; display: inline-flex; align-items: center; justify-content: center; pointer-events: none; box-shadow: 0 0 0 2px #ffffff; }
.submenu-content .nav-button .nav-badge { margin-left: auto; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 999px; background: #ef4444; color: #fff; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; line-height: 1; }
.submenu-content .submenu-hint { font-size: 11.5px; color: #64748b; padding: 6px 12px; line-height: 1.4; }
/* Rail buttons sit inside the (full-width) tooltip wrapper: keep them centred on the rail */
.sidebar-icons .icon-btn { margin-left: auto; margin-right: auto; }

/* Scrollbars for submenu */
.submenu-content::-webkit-scrollbar { width: 6px; }
.submenu-content::-webkit-scrollbar-thumb {
  background: rgba(99, 102, 241, 0.22);
  border-radius: 999px;
}
.submenu-content::-webkit-scrollbar-thumb:hover {
  background: rgba(99, 102, 241, 0.45);
}

/* Content shifting (desktop only) */
.content.shifted {
  margin-left: calc(var(--sidebar-icons-w) + var(--sidebar-sub-w));
  max-width: calc(100vw - (var(--sidebar-icons-w) + var(--sidebar-sub-w)));
}
.content.not-shifted {
  margin-left: var(--sidebar-icons-w);
  max-width: calc(100vw - var(--sidebar-icons-w));
}
.content {
  flex: 1;
  min-width: 0; /* let overflow-x:auto children scroll internally instead of stretching the page */
  padding-top: var(--topbar-h);
  transition: margin-left .2s cubic-bezier(.2, .8, .2, 1);
  max-width: calc(100vw - var(--sidebar-icons-w));
}

/* ==================== NIGHTSCAN RIGHT DRAWER ==================== */
.rightpanel {
  position: fixed;
  top: var(--topbar-h);
  right: 0;
  bottom: 0;
  width: 360px;
  max-width: 92vw;
  background: #fff;
  color: #0f172a;
  border-left: 1px solid rgba(0,0,0,.1);
  box-shadow: -2px 0 12px rgba(0,0,0,.12);
  transform: translateX(100%);
  transition: transform .22s ease;
  z-index: 1040;
  display: flex;
  flex-direction: column;
}
.rightpanel--open { transform: translateX(0); }
.rightpanel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: #f8fafc;
}
.rightpanel__title { font-weight: 600; }
.rightpanel__body { padding: 12px; overflow-y: auto; }
.rightpanel__item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(0,0,0,.08);
  background: #fff;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all .15s ease;
}
.rightpanel__item:hover {
  border-color: #0015ce;
  background: #0312e71a;
  transform: translateX(-4px);
  box-shadow: 0 2px 8px rgba(0, 21, 206, 0.15);
}

.rightpanel__item:active {
  transform: translateX(-2px);
}
.status-badge {
  display: inline-block;
  color: #fff;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}
.rightpanel__empty {
  padding: 24px;
  color: #64748b;
  text-align: center;
}
.backdrop-right {
  position: fixed;
  top: var(--topbar-h);
  left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.45);
  z-index: 1035;
}

/* ==================== MOBILE BOTTOM TAB BAR ==================== */
@media (max-width: 768px) {
  .mobile-bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: var(--mobile-bottom-nav-h);
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 0 8px;
    z-index: 1030;
    background: rgba(255, 255, 255, 0.88);
    -webkit-backdrop-filter: blur(18px) saturate(170%);
    backdrop-filter: blur(18px) saturate(170%);
    border-top: 1px solid var(--tx-hair);
    box-shadow: 0 -12px 30px -22px rgba(15, 23, 42, 0.45);
  }
  .mobile-bottom-nav::before {
    content: "";
    position: absolute;
    left: 0; right: 0; top: -1px;
    height: 2px;
    background: var(--tx-line);
    opacity: 0.8;
    pointer-events: none;
  }

  .mobile-tab {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin: 0 2px;
    padding: 8px 4px;
    background: transparent;
    border: none;
    border-radius: 14px;
    color: var(--sidebar-icon-color);
    cursor: pointer;
    transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }
  .mobile-tab:active { transform: scale(0.95); }
  .mobile-tab.active {
    color: #4338ca;
    background: var(--tx-grad-soft);
    box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.22);
  }
  .mobile-tab.active::before {
    content: "";
    position: absolute;
    top: 3px;
    width: 18px; height: 3px;
    border-radius: 3px;
    background: var(--tx-grad);
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.8);
  }

  .mobile-tab-label {
    max-width: 100%;
    font-size: 10.5px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-tab-badge {
    position: absolute;
    top: 6px;
    right: 8px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--danger);
    color: #fff;
    font-size: 10px;
    border-radius: 999px;
    line-height: 1;
  }

  /* Mobile menu sheet (slides up from the bar) */
  .mobile-menu-modal {
    position: fixed;
    bottom: var(--mobile-bottom-nav-h);
    left: 0;
    right: 0;
    max-height: calc(85vh - var(--mobile-bottom-nav-h));
    display: flex;
    flex-direction: column;
    z-index: 1029;
    background: rgba(255, 255, 255, 0.96);
    -webkit-backdrop-filter: blur(18px) saturate(170%);
    backdrop-filter: blur(18px) saturate(170%);
    border: 1px solid var(--tx-hair-strong);
    border-bottom: none;
    border-radius: 22px 22px 0 0;
    box-shadow: 0 -18px 40px -20px rgba(15, 23, 42, 0.4);
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .mobile-menu-modal.open { transform: translateY(0); }

  .mobile-menu-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--tx-hair);
    border-radius: 22px 22px 0 0;
  }
  .mobile-menu-header::after {
    content: "";
    position: absolute;
    left: 20px; bottom: -1px;
    width: 46px; height: 2px;
    border-radius: 2px;
    background: var(--tx-grad);
  }

  .mobile-menu-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: var(--tx-mono);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #4338ca;
  }

  .mobile-menu-close {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background: transparent;
    border: 1px solid var(--topbar-btn-border);
    border-radius: 10px;
    color: var(--sidebar-icon-color);
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  .mobile-menu-close:hover {
    background: var(--sidebar-btn-hover);
    border-color: var(--tx-hair-strong);
  }

  .mobile-menu-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: visible;
    padding: 16px;
  }

  /* Language dropdown in mobile needs proper positioning */
  .mobile-menu-content .dropdown-menu {
    position: absolute !important;
    top: auto !important;
    bottom: 100% !important;
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) translateY(-8px) !important;
    margin: 0 !important;
    z-index: 9999 !important;
    max-height: 300px;
    overflow-y: auto;
    padding: 10rem .5rem;
  }
  .mobile-menu-content .dropdown { position: relative !important; }

  .mobile-menu-section { margin-bottom: 20px; }

  .mobile-menu-section-title {
    margin-bottom: 8px;
    padding: 8px 12px 6px;
    border-bottom: 1px solid var(--tx-hair);
    font-family: var(--tx-mono);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #6366f1;
  }

  /* Sub-heading of a group inside a mobile section */
  .mobile-menu-sublabel {
    padding: 14px 12px 2px;
    font-family: var(--tx-mono);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #94a3b8;
  }

  .mobile-menu-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    margin-top: 4px;
    padding: 13px 12px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 12px;
    color: #334155;
    font-size: 15px;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  }
  .mobile-menu-item:active { transform: scale(0.98); }
  .mobile-menu-item:hover {
    background: rgba(99, 102, 241, 0.06);
    color: #0f172a;
  }
  .mobile-menu-item.active {
    background: var(--tx-grad-soft);
    border-color: rgba(99, 102, 241, 0.24);
    color: #312e81;
    font-weight: 600;
  }
  .mobile-menu-item.active::before {
    content: "";
    position: absolute;
    left: -1px; top: 10px; bottom: 10px;
    width: 3px;
    border-radius: 3px;
    background: var(--tx-grad);
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.75);
  }
  .mobile-menu-item svg {
    min-width: 22px;
    color: #6366f1;
  }

  /* Profile sheet */
  .mobile-profile-header--action { cursor: pointer; }
  .mobile-profile-header--action:active { opacity: 0.85; }
  .mobile-profile-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    border-bottom: 1px solid var(--tx-hair);
  }
  .mobile-profile-avatar {
    width: 50px;
    height: 50px;
    padding: 2px;
    border-radius: 50%;
    background: var(--tx-grad);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6366f1;
    box-shadow: 0 10px 22px -12px rgba(99, 102, 241, 0.8);
  }
  .mobile-profile-avatar > svg {
    width: 100%;
    height: 100%;
    padding: 11px;
    border-radius: 50%;
    background: #ffffff;
  }
  .mobile-profile-info { flex: 1; }
  .mobile-profile-name {
    margin-bottom: 2px;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }
  .mobile-profile-role {
    font-family: var(--tx-mono);
    font-size: 11px;
    letter-spacing: 0.16em;
    color: #6366f1;
  }

  .mobile-menu-divider {
    height: 1px;
    margin: 12px 0;
    background: var(--tx-hair);
  }

  .content {
    margin-left: 0 !important;
    max-width: 100vw !important;
    padding-bottom: calc(var(--mobile-bottom-nav-h) + 20px) !important;
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.35);
    z-index: 1028;
    backdrop-filter: blur(4px);
  }

  /* Hide desktop sidebar on mobile */
  .sidebar-pro { display: none; }
}

/* ==================== REDUCED MOTION ==================== */
/* Only the decorative loops stop; menu transitions stay (they are functional feedback). */
@media (prefers-reduced-motion: reduce) {
  .topbar::after,
  .psw-chip-dot::after {
    animation: none !important;
  }
}

/* ==================== DARK MODE · VACWAY BLUE CHROME ==================== */
/* Same Ecosystem chrome re-tinted in the VACWAY blues (#0b97ff · #067ddb on #022040 / #01315f navy). Only the shell
   chrome goes dark (topbar, rail, submenu, mobile nav, breadcrumbs, palette, toasts); the page content stays light. */
.dark-mode {
  --bg-primary: #0f172a;
  --bg-secondary: #1e293b;
  --text-primary: #f9fafb;
  --text-secondary: #d1d5db;
  --border-color: #374151;
  --dark-gradient: linear-gradient(135deg,#022040,#0a4080);
  --focus-ring: 0 0 0 3px rgba(11, 151, 255, 0.5);

  --tx-cyan: #4fb6ff;
  --tx-indigo: #0b97ff;
  --tx-violet: #067ddb;
  --tx-grad: linear-gradient(135deg, #4fb6ff 0%, #0b97ff 50%, #067ddb 100%);
  --tx-grad-soft: linear-gradient(135deg, rgba(79, 182, 255, 0.14), rgba(11, 151, 255, 0.18) 55%, rgba(6, 125, 219, 0.16));
  --tx-line: linear-gradient(90deg, transparent 0%, #4fb6ff 22%, #0b97ff 50%, #067ddb 78%, transparent 100%);
  --tx-glass: rgba(2, 32, 64, 0.88);
  --tx-hair: rgba(124, 196, 255, 0.13);
  --tx-hair-strong: rgba(11, 151, 255, 0.4);
  --tx-glow: 0 0 0 1px rgba(11, 151, 255, 0.4), 0 8px 22px -8px rgba(11, 151, 255, 0.7);

  --topbar-bg: var(--tx-glass);
  --topbar-text: #e6f1ff;
  --topbar-border: var(--tx-hair);
  --topbar-btn-border: rgba(124, 196, 255, 0.2);
  --topbar-btn-hover: rgba(11, 151, 255, 0.14);
  --topbar-icon-color: #9db8d9;

  --sidebar-bg: #022040;
  --sidebar-icons-bg: #022040;
  --sidebar-border: var(--tx-hair);
  --sidebar-text: #f1f7ff;
  --sidebar-text-secondary: #b8cbe3;
  --sidebar-icon-color: #8fabcf;
  --sidebar-icon-active: #7cc4ff;
  --sidebar-btn-hover: rgba(11, 151, 255, 0.12);
  --sidebar-btn-active: rgba(11, 151, 255, 0.18);
  --sidebar-submenu-bg: #022040;
  --sidebar-submenu-label: #7cc4ff;
  --sidebar-submenu-border: var(--tx-hair);
}

/* Topbar */
.dark-mode .topbar { box-shadow: inset 0 1px 0 rgba(124, 196, 255, 0.08), 0 10px 30px -18px rgba(0, 0, 0, 0.6); }
.dark-mode .topbar__sep { background: linear-gradient(to bottom, transparent, rgba(79, 182, 255, 0.55), transparent); }
.dark-mode .psw-chip {
  background: linear-gradient(#01315f, #01315f) padding-box, var(--tx-grad) border-box;
  color: #e6f1ff;
  box-shadow: 0 6px 16px -10px rgba(11, 151, 255, 0.85);
}
.dark-mode .psw-chip-label {
  background: linear-gradient(135deg, #cdeaff 0%, #7cc4ff 50%, #2fa6ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
}
.dark-mode .psw-chip-chevron { color: #7cc4ff; }
.dark-mode .psw-chip-panel {
  background: rgba(2, 32, 64, 0.96);
  box-shadow: 0 28px 56px -24px rgba(0, 0, 0, 0.7), 0 0 0 4px rgba(11, 151, 255, 0.08);
}
.dark-mode .psw-chip-panel::before { color: #6f8fb5; }
.dark-mode .psw-chip-card { color: #e6f1ff !important; }
.dark-mode .psw-chip-card:hover { background: rgba(11, 151, 255, 0.14); }
.dark-mode .psw-chip-card::after { color: #6f8fb5; }
.dark-mode .psw-chip-card:hover::after { color: #7cc4ff; }
.dark-mode .topbar .btn-ghost,
.dark-mode .lang-toggle.nav-link,
.dark-mode .cur-toggle.nav-link,
.dark-mode .dark-mode-toggle {
  background: rgba(255, 255, 255, 0.05);
  color: #e6f1ff;
}
.dark-mode .topbar .btn-ghost:hover,
.dark-mode .lang-toggle.nav-link:hover,
.dark-mode .cur-toggle.nav-link:hover,
.dark-mode .dark-mode-toggle:hover {
  background: rgba(11, 151, 255, 0.16);
  color: #ffffff;
}
.dark-mode .topbar .logout-btn { color: #b8cbe3; }
.dark-mode .topbar .logout-btn:hover {
  color: #fb7185;
  border-color: rgba(251, 113, 133, 0.45);
  box-shadow: 0 0 0 1px rgba(251, 113, 133, 0.25), 0 8px 20px -10px rgba(225, 29, 72, 0.7);
}
.dark-mode .user-avatar > img,
.dark-mode .user-avatar > span { background: #01315f; color: #7cc4ff; }
.dark-mode .tx-clock-time { color: #e6f1ff; }
.dark-mode .tx-clock-date { color: #6f8fb5; }

/* Rail */
.dark-mode .sidebar-icons {
  background:
    radial-gradient(rgba(124, 196, 255, 0.12) 1px, transparent 1.2px) 0 0 / 14px 14px,
    linear-gradient(180deg, #022040 0%, #01315f 100%);
  box-shadow: 8px 0 26px -18px rgba(0, 0, 0, 0.7);
}
.dark-mode .sidebar-icons:hover { scrollbar-color: rgba(79, 182, 255, 0.35) transparent; }
.dark-mode .sidebar-icons:hover::-webkit-scrollbar-thumb { background: rgba(79, 182, 255, 0.35); }
.dark-mode .sidebar-icons::-webkit-scrollbar-thumb:hover { background: rgba(79, 182, 255, 0.6); }
.dark-mode .icon-btn:hover { color: #7cc4ff; }
.dark-mode .icon-btn:hover .icon-tile {
  background: rgba(11, 151, 255, 0.14);
  border-color: rgba(79, 182, 255, 0.3);
}
.dark-mode .icon-btn.active { color: #cdeaff; }
.dark-mode .icon-btn.active .icon-tile { box-shadow: 0 10px 20px -10px rgba(11, 151, 255, 0.95), 0 0 0 3px rgba(11, 151, 255, 0.18); }
.dark-mode .icon-btn.active::before { box-shadow: 0 0 12px rgba(11, 151, 255, 0.95); }
.dark-mode .icon-btn:focus-visible .icon-tile { box-shadow: 0 0 0 2px #022040, 0 0 0 4px rgba(79, 182, 255, 0.75); }
.dark-mode .rail-badge { box-shadow: 0 0 0 2px #022040; }

/* Submenu panel */
.dark-mode .submenu-panel {
  background: rgba(2, 32, 64, 0.95);
  box-shadow: 22px 0 44px -26px rgba(0, 0, 0, 0.75);
}
.dark-mode .submenu-label { color: #7cc4ff; }
.dark-mode .submenu-label::before { box-shadow: 0 0 8px rgba(11, 151, 255, 0.9); }
.dark-mode .submenu-sublabel { color: #6f8fb5; }
.dark-mode .submenu-content .submenu-hint { color: #8fabcf; }
.dark-mode .submenu-content .nav-button { color: #c4d5ea; }
.dark-mode .submenu-content .nav-button svg { color: #8fabcf; }
.dark-mode .submenu-content .nav-button:hover {
  background: rgba(11, 151, 255, 0.12);
  border-color: rgba(79, 182, 255, 0.22);
  color: #ffffff;
}
.dark-mode .submenu-content .nav-button:hover svg { color: #7cc4ff; }
.dark-mode .submenu-content .nav-button.active { border-color: rgba(79, 182, 255, 0.32); color: #ffffff; }
.dark-mode .submenu-content .nav-button.active svg { color: #7cc4ff; }
.dark-mode .submenu-content .nav-button.active::before { box-shadow: 0 0 8px rgba(11, 151, 255, 0.9); }
.dark-mode .submenu-content .nav-button:focus-visible { outline-color: #4fb6ff; }
.dark-mode .submenu-content::-webkit-scrollbar-thumb { background: rgba(79, 182, 255, 0.25); }
.dark-mode .submenu-content::-webkit-scrollbar-thumb:hover { background: rgba(79, 182, 255, 0.5); }

/* Mobile bottom bar and sheets */
@media (max-width: 768px) {
  .dark-mode .mobile-bottom-nav {
    background: rgba(2, 32, 64, 0.92);
    box-shadow: 0 -12px 30px -18px rgba(0, 0, 0, 0.7);
  }
  .dark-mode .mobile-tab.active { color: #cdeaff; box-shadow: inset 0 0 0 1px rgba(79, 182, 255, 0.3); }
  .dark-mode .mobile-tab.active::before { box-shadow: 0 0 8px rgba(11, 151, 255, 0.95); }
  .dark-mode .mobile-menu-modal {
    background: rgba(2, 32, 64, 0.97);
    box-shadow: 0 -18px 40px -20px rgba(0, 0, 0, 0.75);
  }
  .dark-mode .mobile-menu-title { color: #cdeaff; }
  .dark-mode .mobile-menu-section-title { color: #7cc4ff; }
  .dark-mode .mobile-menu-sublabel { color: #6f8fb5; }
  .dark-mode .mobile-menu-item { color: #c4d5ea; }
  .dark-mode .mobile-menu-item:hover { background: rgba(11, 151, 255, 0.12); color: #ffffff; }
  .dark-mode .mobile-menu-item.active { border-color: rgba(79, 182, 255, 0.32); color: #ffffff; }
  .dark-mode .mobile-menu-item.active::before { box-shadow: 0 0 8px rgba(11, 151, 255, 0.9); }
  .dark-mode .mobile-menu-item svg { color: #7cc4ff; }
  .dark-mode .mobile-profile-name { color: #f1f7ff; }
  .dark-mode .mobile-profile-avatar { box-shadow: 0 10px 22px -12px rgba(11, 151, 255, 0.9); }
  .dark-mode .backdrop { background: rgba(1, 12, 26, 0.55); }
  .dark-mode .mobile-profile-avatar > svg { background: #01315f; color: #7cc4ff; }
  .dark-mode .mobile-profile-role { color: #7cc4ff; }
}

/* Breadcrumbs, command palette and toasts on the dark chrome */
.dark-mode .breadcrumbs {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}
.dark-mode .breadcrumbs-link,
.dark-mode .breadcrumbs-text {
  color: var(--text-secondary);
}
.dark-mode .breadcrumbs-current {
  color: var(--text-primary);
}
.dark-mode .breadcrumbs-link:hover {
  color: #7cc4ff;
}
.dark-mode .command-palette {
  background: var(--dark-gradient);
  border: 1px solid var(--border-color);
}
.dark-mode .command-palette-input {
  color: var(--text-primary);
}
.dark-mode .command-palette-input::placeholder {
  color: var(--text-secondary);
}
.dark-mode .command-palette-item-label {
  color: var(--text-primary);
}
.dark-mode .command-palette-item-description {
  color: var(--text-secondary);
}
.dark-mode .command-palette-item:hover,
.dark-mode .command-palette-item.selected {
  background: rgba(255, 255, 255, 0.05);
}
.dark-mode .toast-item {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}
.dark-mode .toast-title {
  color: var(--text-primary);
}


/* ==================== COMMAND PALETTE ==================== */
.command-palette-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2000;
  animation: fadeIn 0.15s ease;
}

.command-palette {
  position: fixed;
  top: 15vh;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 640px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: 2001;
  overflow: hidden;
  animation: slideDown 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from { 
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to { 
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.command-palette-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.command-palette-search-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #111827;
  background: transparent;
}

.command-palette-input::placeholder {
  color: #9ca3af;
}

.command-palette-kbd {
  padding: 4px 8px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  font-weight: 600;
}

.command-palette-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.command-palette-content::-webkit-scrollbar { width: 8px; }
.command-palette-content::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 999px;
}
.command-palette-content::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.command-palette-empty {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.command-palette-group {
  margin-bottom: 12px;
}

.command-palette-group-label {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
}

.command-palette-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;
}

.command-palette-item:hover,
.command-palette-item.selected {
  background: #f3f4f6;
  transform: translateX(2px);
}

.command-palette-item:active {
  transform: scale(0.99);
}

.command-palette-item-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.command-palette-item-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-item-content {
  flex: 1;
  min-width: 0;
}

.command-palette-item-label {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 2px;
}

.command-palette-item-description {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.command-palette-item-arrow {
  color: #9ca3af;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.command-palette-item:hover .command-palette-item-arrow,
.command-palette-item.selected .command-palette-item-arrow {
  opacity: 1;
}

.command-palette-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  color: #6b7280;
  background: transparent;
}

.command-palette-footer-item {
  display: flex;
  align-items: center;
  gap: 6px;
  display: none;
}

.command-palette-footer kbd {
  padding: 2px 6px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

/* ==================== BREADCRUMBS ==================== */
.breadcrumbs {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 24px;
  position: sticky;
  top: var(--topbar-h);
  z-index: 100;
}

.breadcrumbs-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.breadcrumbs-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.breadcrumbs-link,
.breadcrumbs-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.15s ease, transform 0.15s ease;
}

.breadcrumbs-link:hover {
  color: #3b82f6;
  transform: translateY(-1px);
}

.dark-mode .breadcrumbs-link:hover {
  color: #60a5fa;
}

.breadcrumbs-link:active {
  transform: translateY(0);
}

.breadcrumbs-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 4px;
}

.breadcrumbs-current {
  color: #111827;
  font-weight: 500;
}

.breadcrumbs-icon {
  flex-shrink: 0;
}

.breadcrumbs-separator {
  color: #d1d5db;
  flex-shrink: 0;
}

/* ==================== ENHANCED TOOLTIP ==================== */
.enhanced-tooltip {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  animation: tooltipFadeIn 0.15s ease;
}

@keyframes tooltipFadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.enhanced-tooltip-content {
  background: #1f2937;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 280px;
}

.enhanced-tooltip--rich .enhanced-tooltip-content {
  padding: 10px 14px;
}

.enhanced-tooltip-simple {
  white-space: nowrap;
}

.enhanced-tooltip-rich {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.enhanced-tooltip-title {
  font-weight: 600;
  font-size: 13px;
}

.enhanced-tooltip-description {
  font-size: 12px;
  color: #d1d5db;
  line-height: 1.5;
}

.enhanced-tooltip-shortcut {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.enhanced-tooltip-kbd {
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

.enhanced-tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}

.enhanced-tooltip-arrow--top {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0 6px;
  border-color: #1f2937 transparent transparent transparent;
}

.enhanced-tooltip-arrow--bottom {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 0 6px 6px 6px;
  border-color: transparent transparent #1f2937 transparent;
}

.enhanced-tooltip-arrow--left {
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 0 6px 6px;
  border-color: transparent transparent transparent #1f2937;
}

.enhanced-tooltip-arrow--right {
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 6px 6px 0;
  border-color: transparent #1f2937 transparent transparent;
}

/* ==================== TOAST NOTIFICATIONS ==================== */
.toast-container {
  position: fixed;
  top: calc(var(--topbar-h) + 20px);
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.toast-item {
  pointer-events: all;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 320px;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px var(--toast-border);
  animation: toastSlideIn 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
  transition: all 0.2s ease;
}

.toast-item:hover {
  transform: translateX(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2), 0 0 0 1px var(--toast-border);
}

@keyframes toastSlideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  margin-bottom: 2px;
}

.toast-message {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.toast-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  background: #f3f4f6;
  color: #111827;
  transform: scale(1.1);
}

.toast-close:active {
  transform: scale(0.95);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .toast-container {
    right: 12px;
    left: 12px;
    top: calc(var(--topbar-h) + 12px);
  }
  
  .toast-item {
    min-width: 0;
    max-width: 100%;
  }
}

/* ==================== LANGUAGE SELECTOR ==================== */
/* Pill que imita al .dark-mode-toggle: mismo borde, hover y elevación; la
   bandera hace de "thumb" redondo. (Mismo bloque que el AppShell de lockers.) */
.lang-toggle.nav-link {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  border-radius: 20px;
  padding: 4px 10px 4px 4px !important;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex !important;
  align-items: center;
  color: var(--topbar-text);
}

.lang-toggle.nav-link:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}

.lang-toggle.nav-link:active {
  transform: translateY(0);
}

.lang-toggle.nav-link:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.lang-toggle .lang-flag {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
/* ==================== CURRENCY SELECTOR ==================== */
/* Mismo pill que .lang-toggle; el símbolo de la divisa hace de thumb redondo. */
.cur-toggle.nav-link {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  border-radius: 20px;
  padding: 4px 10px 4px 4px !important;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex !important;
  align-items: center;
  gap: 6px;
  color: var(--topbar-text);
}
.cur-toggle.nav-link:hover { background: var(--topbar-btn-hover); transform: translateY(-1px); }
.cur-toggle.nav-link:active { transform: translateY(0); }
.cur-toggle.nav-link:focus { outline: none; box-shadow: var(--focus-ring); }
.cur-toggle .cur-thumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #fff;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.cur-toggle .cur-code { font-size: 12px; font-weight: 600; letter-spacing: .02em; }
.cur-menu.dropdown-menu { min-width: 270px; padding: 6px 0; }
.cur-search { padding: 4px 8px 8px; border-bottom: 1px solid rgba(0, 0, 0, .08); }
.cur-search input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  color: #1e293b;
  background: #fff;
  outline: none;
}
.cur-search input:focus { border-color: #6366f1; box-shadow: 0 0 0 2px rgba(99, 102, 241, .2); }
.cur-list { max-height: 320px; overflow-y: auto; }
.cur-item.dropdown-item { display: flex; align-items: center; gap: 8px; padding: 6px 12px; font-size: 13px; cursor: pointer; }
.cur-item--active.dropdown-item { background: rgba(99, 102, 241, .10); font-weight: 600; }
.cur-item .cur-item-symbol { min-width: 36px; text-align: center; font-weight: 700; color: #1e293b; background: #f1f5f9; border-radius: 6px; padding: 2px 4px; font-size: 12px; }
.cur-item .cur-item-code { font-weight: 600; min-width: 36px; }
.cur-item .cur-item-name { color: #64748b; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cur-item .cur-item-default { color: #f59e0b; font-size: 12px; }
.cur-empty { padding: 10px 12px; font-size: 13px; color: #64748b; }
@media (max-width: 768px) {
  .mobile-menu-content .cur-menu.dropdown-menu { padding: .5rem !important; width: min(92vw, 320px); }
}
  
/* ==================== DARK MODE TOGGLE ==================== */
/* Glass key like the other topbar keys */
.dark-mode-toggle {
  height: 36px;
  padding: 0 7px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--topbar-btn-border);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, background 0.15s ease;
  display: flex;
  align-items: center;
}

.dark-mode-toggle:hover {
  background: #ffffff;
  border-color: var(--tx-hair-strong);
  box-shadow: var(--tx-glow);
  transform: translateY(-1px);
}

.dark-mode-toggle:active {
  transform: translateY(0);
}

.dark-mode-toggle:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.dark-mode-toggle-track {
  width: 44px;
  height: 22px;
  border-radius: 11px;
  background: rgba(99, 102, 241, 0.12);
  position: relative;
  transition: background 0.3s ease;
}

.dark-mode-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-mode-icon {
  color: #f59e0b;
}

/* Dark mode styles */
.dark-mode .dark-mode-toggle-track {
  background: rgba(11, 151, 255, 0.3);
}

.dark-mode .dark-mode-toggle-thumb {
  left: 24px;
  background: #0b97ff;
}

.dark-mode .dark-mode-icon {
  color: #fff;
}

/* ==================== SELECTOR KEYS (language / currency) ==================== */
.lang-toggle.nav-link,
.cur-toggle.nav-link {
  height: 36px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--topbar-btn-border);
  border-radius: 10px;
  color: #0f172a;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, background 0.15s ease;
}
.lang-toggle.nav-link:hover,
.cur-toggle.nav-link:hover {
  background: #ffffff;
  border-color: var(--tx-hair-strong);
  box-shadow: var(--tx-glow);
  transform: translateY(-1px);
}
.cur-toggle .cur-code { font-family: var(--tx-mono); letter-spacing: 0.08em; }
.cur-menu.dropdown-menu {
  border: 1px solid var(--tx-hair-strong);
  border-radius: 12px;
  box-shadow: 0 24px 48px -24px rgba(15, 23, 42, 0.4);
}

/* ==================== INDICATOR KEYS (Nightscan / Tickets / Incidencias) ==================== */
/* Same glass key as .btn-ghost; Tickets (blue) and Incidencias (orange) keep their accent on hover. */
.nightscan-btn,
.tickets-indicator-btn,
.incidences-indicator-btn {
  height: 36px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--topbar-btn-border);
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, background 0.15s ease;
}
.nightscan-btn:hover {
  background: #ffffff;
  border-color: var(--tx-hair-strong);
  box-shadow: var(--tx-glow);
}
.tickets-indicator-btn:hover {
  background: #ffffff;
  border-color: rgba(59, 130, 246, 0.55);
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.22), 0 8px 20px -10px rgba(59, 130, 246, 0.6);
}
.incidences-indicator-btn:hover {
  background: #ffffff;
  border-color: rgba(249, 115, 22, 0.55);
  box-shadow: 0 0 0 1px rgba(249, 115, 22, 0.22), 0 8px 20px -10px rgba(249, 115, 22, 0.6);
}
.nightscan-btn:focus-visible,
.tickets-indicator-btn:focus-visible,
.incidences-indicator-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }
/* Loading badge: white spinner on the accent colour (readable on the light key too) */
.nightscan-badge--loading { background: var(--tx-indigo); }

.dark-mode .topbar .nightscan-btn,
.dark-mode .topbar .tickets-indicator-btn,
.dark-mode .topbar .incidences-indicator-btn {
  background: rgba(255, 255, 255, 0.05);
  color: #e6f1ff;
}
.dark-mode .topbar .nightscan-btn:hover { background: rgba(11, 151, 255, 0.16); color: #ffffff; }
.dark-mode .topbar .tickets-indicator-btn:hover { background: rgba(59, 130, 246, 0.16); color: #ffffff; }
.dark-mode .topbar .incidences-indicator-btn:hover { background: rgba(249, 115, 22, 0.14); color: #ffffff; }
  `;
  const el = document.createElement("style");
  el.id = "app-shell-styles";
  el.innerHTML = css;
  document.head.appendChild(el);
};

/**
 * TopBar Component
 */
// Topbar user badges — same labels and colours as the lockers client topbar.
const ADMIN_TYPE_BADGES = {
  MASTER:       { label: 'MASTER',       bg: 'linear-gradient(90deg,#f43f5e,#9f1239)' },
  PROYECTOS:    { label: 'PROYECTOS',    bg: 'linear-gradient(90deg,#6366f1,#4338ca)' },
  SAT1:         { label: 'SAT 1',        bg: 'linear-gradient(90deg,#0ea5e9,#0369a1)' },
  SAT2:         { label: 'SAT 2',        bg: 'linear-gradient(90deg,#06b6d4,#0e7490)' },
  PRODUCCION:   { label: 'PRODUCCIÓN',   bg: 'linear-gradient(90deg,#64748b,#334155)' },
  PROVEEDOR:    { label: 'PROVEEDOR',    bg: 'linear-gradient(90deg,#a3a3a3,#525252)' },
  CONTABILIDAD: { label: 'CONTABILIDAD', bg: 'linear-gradient(90deg,#10b981,#065f46)' },
  COMERCIAL:    { label: 'COMERCIAL',    bg: 'linear-gradient(90deg,#f59e0b,#b45309)' },
  MARKETING:    { label: 'MARKETING',    bg: 'linear-gradient(90deg,#ec4899,#be185d)' },
  PROGRAMMER:   { label: 'PROGRAMMER',   bg: 'linear-gradient(90deg,#22c55e,#15803d)' },
};
const USER_TYPE_BADGES = {
  LICENCIA_PRO:    { label: 'PRO',          bg: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' },
  LICENCIA_BASICA: { label: 'BASIC',        bg: 'rgba(100,116,139,.25)', color: 'var(--topbar-text,#fff)' },
  PDA:             { label: 'OPERATIVO',    bg: 'linear-gradient(90deg,#3b82f6,#1d4ed8)' },
  CASH:            { label: 'CASH-CONTROL', bg: 'linear-gradient(90deg,#10b981,#065f46)' },
  MANAGER:         { label: 'MANAGER',      bg: 'linear-gradient(90deg,#8b5cf6,#5b21b6)' },
  GROUP:           { label: 'GROUP',        bg: 'linear-gradient(90deg,#f59e0b,#b45309)' },
};
const TopbarUserBadge = ({ role, type }) => {
  const badge = (role === 'ADMIN' && ADMIN_TYPE_BADGES[type]) || USER_TYPE_BADGES[type];
  if (!badge) return null;
  return (
    <span style={{ marginLeft: 7, padding: '2px 7px', borderRadius: 20, background: badge.bg, color: badge.color || '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1, verticalAlign: 'middle', lineHeight: 1.5 }}>{badge.label}</span>
  );
};

/** Mono clock readout of the topbar, as in the Ecosystem (refreshes every 15 s; minutes are enough). */
const TopbarClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="tx-clock notranslate" translate="no" aria-hidden="true">
      <span className="tx-clock-time">{now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
      <span className="tx-clock-date">{now.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' })}</span>
    </div>
  );
};

const TopBar = ({
  fullname,
  userId,
  onLogout,
  isMobile,
  role,
  type,
  category,
  openTicketsCount,
  openIncidencesCount,
  push,
  onToggleSidebarMobile,
  onOpenCommandPalette,
  onGoToUser
}) => {
  const [showChipMenu, setShowChipMenu] = useState(false);
  const chipCloseTimer = useRef(null);

  const PLATFORMS = [
    { label: 'LOCKERS', href: 'https://lockers.vacway.es', color: '#3b82f6' },
    { label: 'SELF-SERVICE POS', href: 'https://ticketing.vacway.es', color: '#f97316' },
    ...(role === 'ADMIN' ? [
      { label: 'HUB', href: 'https://clock.vacway.es', color: '#06b6d4' },
      { label: 'REPORTING', href: 'https://reporting.vacway.es', color: '#ec4899' },
    ] : []),
  ];

  // Show tickets indicator for MASTER and SAT1
  const showTicketsIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT1');
  // Show incidences indicator for MASTER and SAT2
  const showIncidencesIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT2');

  return (
    <div className="topbar">
      <div className="topbar__inner">
        <div className="topbar__left">
          <Link to="/" className="topbar__brand" aria-label="Home">
            <img src="/images/logo_white.png" alt="Logo" className="logo-dark" />
            <img src="/images/logo_blue.png" alt="Logo" className="logo-light" />
          </Link>
          <span className="topbar__sep" aria-hidden="true" />
          {/* ── Platform switcher ── */}
          <div
            style={{ position: 'relative' }}
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') { clearTimeout(chipCloseTimer.current); setShowChipMenu(true); } }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') { chipCloseTimer.current = setTimeout(() => setShowChipMenu(false), 120); } }}
          >
            <button
              className="psw-chip"
              onClick={() => setShowChipMenu(v => !v)}
              aria-label="Cambiar plataforma"
            >
              <span className="psw-chip-dot" />
              <span className="psw-chip-label">WATERPROOF</span>
              <ChevronDown size={13} className={`psw-chip-chevron${showChipMenu ? ' psw-chip-chevron--open' : ''}`} />
            </button>
            {showChipMenu && (
              <div className="psw-chip-panel">
                {PLATFORMS.map(p => (
                  <a key={p.label} href={p.href} className="psw-chip-card">
                    <span className="psw-chip-card-accent" style={{ background: p.color, color: p.color }} />
                    <span className="psw-chip-card-label">{p.label}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="topbar__right">
          {!isMobile && <TopbarClock />}
          {/* Command Palette trigger */}
          <EnhancedTooltip
            content="Búsqueda rápida"
            description="Buscar y navegar rápidamente"
            shortcut={['⌘K']}
            position="bottom"
          >
            <button
              className="btn-ghost"
              onClick={onOpenCommandPalette}
              title="Búsqueda rápida"
              aria-label="Abrir búsqueda rápida (Cmd+K)"
              style={{ marginRight: 8 }}
            >
              <Search size={18} />
              {!isMobile && <span style={{ marginLeft: 6 }}>Buscar</span>}
            </button>
          </EnhancedTooltip>

          {/* Tickets indicator - MASTER and SAT1 only */}
          {showTicketsIndicator && (
            <EnhancedTooltip
              content="Tickets Abiertos"
              description="Ver tickets de soporte abiertos"
              position="bottom"
            >
              <button
                className="tickets-indicator-btn"
                onClick={() => push('/supporttickets')}
                title="Tickets Abiertos"
                aria-label="Ver tickets abiertos"
              >
                <Ticket size={18} />
                {!isMobile && <span>Tickets</span>}
                {openTicketsCount > 0 && <span className="indicator-badge">{openTicketsCount}</span>}
              </button>
            </EnhancedTooltip>
          )}

          {/* Incidences indicator - MASTER and SAT2 only */}
          {showIncidencesIndicator && (
            <EnhancedTooltip
              content="Incidencias Abiertas"
              description="Ver incidencias abiertas"
              position="bottom"
            >
              <button
                className="incidences-indicator-btn"
                onClick={() => push('/incidences')}
                title="Incidencias Abiertas"
                aria-label="Ver incidencias abiertas"
              >
                <AlertTriangle size={18} />
                {!isMobile && <span>Incidencias</span>}
                {openIncidencesCount > 0 && <span className="indicator-badge">{openIncidencesCount}</span>}
              </button>
            </EnhancedTooltip>
          )}
          
          <div className="mr-2 d-none d-md-flex"><GoogleTranslate /></div>


          
          <div className="mr-2 d-none d-md-flex"><CurrencySelector /></div>

          {/* Dark Mode Toggle */}
          {!isMobile && (
            <div style={{ marginRight: 8 }}>
              <EnhancedTooltip
                content="Tema"
                description="Cambiar entre modo claro y oscuro"
                position="bottom"
              >
                <DarkModeToggle />
              </EnhancedTooltip>
            </div>
          )}

          {/* Desktop-only actions */}
          {!isMobile && (
            <>
              <EnhancedTooltip content={fullname} position="bottom">
                <button type="button" className="btn-ghost user-btn" title={fullname} onClick={() => onGoToUser(userId)}>
                  <span className="user-avatar">
                    {role === 'ADMIN' ? (
                      <img
                        src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                        alt={fullname}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                        }}
                      />
                    ) : (
                      <span><UserIcon size={14} /></span>
                    )}
                  </span>
                  <span className="d-none d-sm-inline">{fullname}</span>
                  <TopbarUserBadge role={role} type={type} />
                </button>
              </EnhancedTooltip>
              <EnhancedTooltip content="Cerrar sesión" position="bottom">
                <button className="btn-ghost logout-btn" onClick={onLogout} style={{ marginLeft: 8 }}>
                  <LogOut size={18} />
                  <span className="d-none d-sm-inline">Salir</span>
                </button>
              </EnhancedTooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar Component (Desktop)
 */
/**
 * Rail hover: hovering a category icon opens its submenu panel at once, over the
 * content; leaving the whole sidebar (rail + panel) closes it. A click pins the
 * panel open (and shifts the content, via setPinned) until the same icon is
 * clicked again or a screen is chosen.
 */
const useRailHover = (activeMenu, setActiveMenu, setPinned = () => {}) => {
  const closeTimer = useRef(null);
  const pinned = useRef(false);
  const pin = (value) => { pinned.current = value; setPinned(value); };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => { if (!activeMenu) pin(false); }, [activeMenu]);
  return {
    toggleMenu: (menu) => {
      clearTimeout(closeTimer.current);
      if (activeMenu === menu && pinned.current) { pin(false); setActiveMenu(null); }
      else { pin(true); setActiveMenu(menu); }
    },
    hoverMenu: (menu) => {
      clearTimeout(closeTimer.current);
      if (activeMenu !== menu) setActiveMenu(menu);
    },
    // Icon without a submenu (direct link / action): hovering it closes the open panel.
    hoverNoMenu: () => {
      clearTimeout(closeTimer.current);
      if (activeMenu) setActiveMenu(null);
    },
    railProps: {
      onMouseEnter: () => clearTimeout(closeTimer.current),
      // Short grace so crossing the rail/panel edge doesn't flicker it closed
      onMouseLeave: () => {
        clearTimeout(closeTimer.current);
        if (!pinned.current) closeTimer.current = setTimeout(() => setActiveMenu(null), 80);
      },
    },
  };
};

const Sidebar = ({ setMenuPinned, role, type, category, userId, onNavigate, activeMenu, setActiveMenu }) => {
  const { toggleMenu, hoverMenu, railProps } = useRailHover(activeMenu, setActiveMenu, setMenuPinned);

  // Check access to menu categories
  const hasPuntosVenta = hasAccessToAny(role, type, category, ['machines', 'minis']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'audit', 'modules', 'matrixs', 'tpvs']);
  const hasVentas = hasAccessToAny(role, type, category, ['sales', 'arqueos', 'arqueosSales', 'objectives', 'comissions']);
  const hasFichajes = hasAccessToAny(role, type, category, ['shifts', 'shiftsAdmin']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'supportchat', 'faqs', 'manuals', 'api-docs']);
  const hasSAT = hasAccessToAny(role, type, category, ['supporttickets', 'incidences', 'slas', 'bowler']);
  const hasAdmin = hasAccessToAny(role, type, category, ['promoters', 'users', 'logistics']);
  const hasTraining = role === 'PROMOTER' || hasAdmin;
  const hasComercial = hasAccessToAny(role, type, category, ['proposal-configurator', 'proposals', 'proposal-prices']);

  const NavLinkItem = ({ to, icon: Icon, children }) => (
    <RRNavLink
      to={to}
      className="nav-button"
      onClick={() => {
        setActiveMenu(null);
        onNavigate();
      }}
    >
      {Icon && <Icon size={16} />}
      <span>{children}</span>
    </RRNavLink>
  );

  return (
    <div className="sidebar-pro" {...railProps}>
      <div className="sidebar-icons">
        {hasPuntosVenta && (
          <EnhancedTooltip disabled content="Canales" description="Gestión de ONEs y MINIs" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "puntos" ? "active" : ""}`}
              onClick={() => toggleMenu("puntos")}
              onMouseEnter={() => hoverMenu("puntos")}
              aria-label="Menú de canales"
            >
              <span className="icon-tile"><Monitor size={19} /></span>
              <span className="icon-label">Canales</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasVentas && (
          <EnhancedTooltip disabled content="Reporte" description="Análisis y reportes de ventas" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "ventas" ? "active" : ""}`}
              onClick={() => toggleMenu("ventas")}
              onMouseEnter={() => hoverMenu("ventas")}
              aria-label="Menú de reporte"
            >
              <span className="icon-tile"><BarChart2 size={19} /></span>
              <span className="icon-label">Reporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasOperativa && (
          <EnhancedTooltip disabled content="Operativa" description="Gestión operativa del sistema" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "operativa" ? "active" : ""}`}
              onClick={() => toggleMenu("operativa")}
              onMouseEnter={() => hoverMenu("operativa")}
              aria-label="Menú de operativa"
            >
              <span className="icon-tile"><SlidersHorizontal size={19} /></span>
              <span className="icon-label">Operativa</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasFichajes && (
          <EnhancedTooltip disabled content="Fichajes" description="Control de jornadas laborales" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "fichajes" ? "active" : ""}`}
              onClick={() => toggleMenu("fichajes")}
              onMouseEnter={() => hoverMenu("fichajes")}
              aria-label="Menú de fichajes"
            >
              <span className="icon-tile"><Clock size={19} /></span>
              <span className="icon-label">Fichajes</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasSAT && (
          <EnhancedTooltip disabled content="SAT" description="Servicio de atención técnica" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "sat" ? "active" : ""}`}
              onClick={() => toggleMenu("sat")}
              onMouseEnter={() => hoverMenu("sat")}
              aria-label="Menú de SAT"
            >
              <span className="icon-tile"><Headset size={19} /></span>
              <span className="icon-label">SAT</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasSoporte && (
          <EnhancedTooltip disabled content="Soporte" description="Recursos de ayuda y soporte" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "soporte" ? "active" : ""}`}
              onClick={() => toggleMenu("soporte")}
              onMouseEnter={() => hoverMenu("soporte")}
              aria-label="Menú de soporte"
            >
              <span className="icon-tile"><HelpCircle size={19} /></span>
              <span className="icon-label">Soporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasTraining && (
          <EnhancedTooltip disabled content="Training" description="Cursos y gestión de formación" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "training" ? "active" : ""}`}
              onClick={() => toggleMenu("training")}
              onMouseEnter={() => hoverMenu("training")}
              aria-label="Menú de training"
            >
              <span className="icon-tile"><NotebookTabs size={19} /></span>
              <span className="icon-label">Training</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasComercial && (
          <EnhancedTooltip disabled content="Comercial" description="Propuestas y configurador comercial" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "comercial" ? "active" : ""}`}
              onClick={() => toggleMenu("comercial")}
              onMouseEnter={() => hoverMenu("comercial")}
              aria-label="Menú comercial"
            >
              <span className="icon-tile"><Briefcase size={19} /></span>
              <span className="icon-label">Comercial</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasAdmin && (
          <EnhancedTooltip disabled content="Administración" description="Configuración y administración" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "admin" ? "active" : ""}`}
              onClick={() => toggleMenu("admin")}
              onMouseEnter={() => hoverMenu("admin")}
              aria-label="Menú de administración"
            >
              <span className="icon-tile"><Crown size={19} /></span>
              <span className="icon-label">Admin</span>
            </button>
          </EnhancedTooltip>
        )}

      </div>

      <div className={`submenu-panel ${activeMenu ? "open" : ""}`}>
        {activeMenu === "puntos" && (
          <div className="submenu-content">
            <div className="submenu-label">Canales</div>
            {canAccess(role, type, category, 'machines') && (
              <NavLinkItem to="/machines" icon={Monitor}>ONEs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'minis') && (
              <NavLinkItem to="/minis" icon={Boxes}>MINIs</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "operativa" && (
          <div className="submenu-content">
            <div className="submenu-label">Operativa</div>
            {(canAccess(role, type, category, 'customers') || canAccess(role, type, category, 'contracts')) && <div className="submenu-sublabel">Clientes</div>}
            {canAccess(role, type, category, 'customers') && (
              <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
            )}
            {canAccess(role, type, category, 'contracts') && (
              <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
            )}
            {(canAccess(role, type, category, 'audit') || canAccess(role, type, category, 'tpvs')) && <div className="submenu-sublabel">Control</div>}
            {canAccess(role, type, category, 'audit') && (
              <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tpvs') && (
              <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "ventas" && (
          <div className="submenu-content">
            <div className="submenu-label">Reporte</div>
            {(canAccess(role, type, category, 'arqueos') || canAccess(role, type, category, 'operational-dashboard')) && <div className="submenu-sublabel">Dashboards</div>}
            {canAccess(role, type, category, 'arqueos') && (
              <>
                <NavLinkItem to="/pro-dashboard" icon={TrendingUp}>Dashboard</NavLinkItem>
              </>
            )}
            {canAccess(role, type, category, 'operational-dashboard') && (
              <NavLinkItem to="/operational-dashboard" icon={Gauge}>Dashboard Operativo</NavLinkItem>
            )}
            {(canAccess(role, type, category, 'sales') || canAccess(role, type, category, 'objectives') || canAccess(role, type, category, 'comissions')) && <div className="submenu-sublabel">Ventas</div>}
            {canAccess(role, type, category, 'sales') && (
              <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'objectives') && (
              <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'comissions') && (
              <NavLinkItem to="/comissions" icon={Calculator}>Comisiones</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "fichajes" && (
          <div className="submenu-content">
            <div className="submenu-label">Fichajes</div>
            {canAccess(role, type, category, 'shifts') && (
              <NavLinkItem to="/shifts" icon={Clock}>Jornada Promotor</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "soporte" && (
          <div className="submenu-content">
            <div className="submenu-label">Soporte</div>
            {(canAccess(role, type, category, 'supportticketadd') || canAccess(role, type, category, 'supportchat')) && <div className="submenu-sublabel">Contacto</div>}
            {canAccess(role, type, category, 'supportticketadd') && (
              <NavLinkItem to="/supportticketadd" icon={Info}>Mis Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'supportchat') && (
              <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
            )}
            {(canAccess(role, type, category, 'faqs') || canAccess(role, type, category, 'manuals') || canAccess(role, type, category, 'api-docs')) && <div className="submenu-sublabel">Documentación</div>}
            {canAccess(role, type, category, 'faqs') && (
              <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'manuals') && (
              <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
            )}
            {canAccess(role, type, category, 'api-docs') && (
              <NavLinkItem to="/api-docs" icon={Newspaper}>API</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "sat" && (
          <div className="submenu-content">
            <div className="submenu-label">SAT</div>
            {(canAccess(role, type, category, 'supporttickets') || canAccess(role, type, category, 'incidences') || canAccess(role, type, category, 'slas')) && <div className="submenu-sublabel">Incidencias</div>}
            {canAccess(role, type, category, 'supporttickets') && (
              <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'incidences') && (
              <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
            )}
            {canAccess(role, type, category, 'slas') && (
              <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
            )}
            {canAccess(role, type, category, 'bowler') && <div className="submenu-sublabel">Seguimiento</div>}
            {canAccess(role, type, category, 'bowler') && (
              <NavLinkItem to="/bowler" icon={BarChart2}>Bowler</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "training" && (
          <div className="submenu-content">
            <div className="submenu-label">Training</div>
            <NavLinkItem to={`/training/${userId}`} icon={BookOpen}>Cursos</NavLinkItem>
            {hasAdmin && (
              <NavLinkItem to="/training-admin" icon={NotebookTabs}>Gestión de Cursos</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "comercial" && (
          <div className="submenu-content">
            <div className="submenu-label">Comercial</div>
            {canAccess(role, type, category, 'proposal-configurator') && (
              <NavLinkItem to="/proposal-configurator" icon={Briefcase}>Configurador</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposals') && (
              <NavLinkItem to="/proposals" icon={FileSignature}>Propuestas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'proposal-prices') && (
              <NavLinkItem to="/proposal-prices" icon={Calculator}>Precios</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "admin" && (
          <div className="submenu-content">
            <div className="submenu-label">Administración</div>
            {(canAccess(role, type, category, 'promoters') || canAccess(role, type, category, 'users')) && <div className="submenu-sublabel">Personas</div>}
            {canAccess(role, type, category, 'promoters') && (
              <NavLinkItem to="/promoters" icon={Users}>Promotores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'users') && (
              <NavLinkItem to="/users" icon={UserCog}>Trabajadores</NavLinkItem>
            )}
            {type === 'MASTER' && <div className="submenu-sublabel">Datos</div>}
            {type === 'MASTER' && (
              <NavLinkItem to="/sync" icon={RefreshCw}>Base de Datos Sync</NavLinkItem>
            )}
            {type === 'MASTER' && (
              <NavLinkItem to="/customer-ids" icon={IdCard}>Clientes · ID</NavLinkItem>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile Menu Modal
 */
const MobileMenuModal = ({
  open,
  onClose,
  title,
  icon: Icon,
  children,
  isProfile = false,
  fullname,
  role
}) => {
  return (
    <>
      {open && <div className="backdrop" onClick={onClose} />}
      <div className={`mobile-menu-modal ${open ? "open" : ""}`}>
        {isProfile ? (
          <div className="mobile-profile-header">
            <div className="mobile-profile-avatar">
              {role === 'ADMIN' ? (
                <img
                  src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                  alt={fullname}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                  }}
                />
              ) : (
                <UserIcon size={24} />
              )}
            </div>
            <div className="mobile-profile-info">
              <div className="mobile-profile-name">{fullname}</div>
              <div className="mobile-profile-role">{role}</div>
            </div>
          </div>
        ) : (
          <div className="mobile-menu-header">
            <div className="mobile-menu-title">
              {Icon && <Icon size={22} />}
              {title}
            </div>
            <button className="mobile-menu-close" onClick={onClose} aria-label="Cerrar menú">
              <ArrowLeft size={22} />
            </button>
          </div>
        )}
        <div className="mobile-menu-content">
          {children}
        </div>
      </div>
    </>
  );
};

/**
 * Mobile Bottom Navigation
 */
const MobileBottomNav = ({
  role,
  type,
  category,
  onNavigate,
  activeTab,
  setActiveTab,
  fullname,
  userId,
  onLogout,
  onGoToUser,
  openTicketsCount,
  openIncidencesCount,
  push
}) => {
  // Configure which sections appear in bottom bar vs "More" menu
  const favoriteMenus = ['puntos', 'ventas', 'operativa']; // Edit this to change favorites

  // Check permissions
  const hasPuntosVenta = hasAccessToAny(role, type, category, ['machines', 'minis']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'audit', 'modules', 'matrixs', 'tpvs']);
  const hasVentas = hasAccessToAny(role, type, category, ['sales', 'arqueos', 'arqueosSales', 'objectives', 'comissions']);
  const hasFichajes = hasAccessToAny(role, type, category, ['shifts', 'shiftsAdmin']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'supportchat', 'faqs', 'manuals', 'api-docs']);
  const hasSAT = hasAccessToAny(role, type, category, ['supporttickets', 'incidences', 'slas', 'bowler']);
  const hasAdmin = hasAccessToAny(role, type, category, ['promoters', 'users', 'logistics']);
  const hasComercial = hasAccessToAny(role, type, category, ['proposal-configurator', 'proposals', 'proposal-prices']);

  const hasTraining = role === 'PROMOTER' || hasAdmin;
  // Show tickets indicator for MASTER and SAT1
  const showTicketsIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT1');
  // Show incidences indicator for MASTER and SAT2
  const showIncidencesIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT2');
  const menuSections = {
    puntos: { has: hasPuntosVenta, icon: Monitor, label: 'Canales', isFavorite: favoriteMenus.includes('puntos') },
    ventas: { has: hasVentas, icon: BarChart2, label: 'Reporte', isFavorite: favoriteMenus.includes('ventas') },
    operativa: { has: hasOperativa, icon: SlidersHorizontal, label: 'Operativa', isFavorite: favoriteMenus.includes('operativa') },
    fichajes: { has: hasFichajes, icon: Clock, label: 'Fichajes', isFavorite: favoriteMenus.includes('fichajes') },
    sat: { has: hasSAT, icon: Headset, label: 'SAT', isFavorite: favoriteMenus.includes('sat') },
    soporte: { has: hasSoporte, icon: HelpCircle, label: 'Soporte', isFavorite: favoriteMenus.includes('soporte') },
    training: { has: hasTraining, icon: NotebookTabs, label: 'Training', isFavorite: favoriteMenus.includes('training') },
    comercial: { has: hasComercial, icon: Briefcase, label: 'Comercial', isFavorite: favoriteMenus.includes('comercial') },
    admin: { has: hasAdmin, icon: Crown, label: 'Admin', isFavorite: favoriteMenus.includes('admin') },
  };

  const favoriteTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && config.isFavorite)
    .map(([key]) => key);

  const moreTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && !config.isFavorite)
    .map(([key]) => key);

  const hasMore = moreTabs.length > 0;

  const NavLinkItem = ({ to, icon: Icon, children }) => (
    <RRNavLink
      to={to}
      className="mobile-menu-item"
      onClick={() => {
        setActiveTab(null);
        onNavigate();
      }}
    >
      {Icon && <Icon size={20} />}
      <span>{children}</span>
    </RRNavLink>
  );

  return (
    <>
      <div className="mobile-bottom-nav">
        {favoriteTabs.map((tab) => {
          const config = menuSections[tab];
          const Icon = config.icon;
          return (
            <button
              key={tab}
              className={`mobile-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(prev => prev === tab ? null : tab)}
            >
              <Icon size={22} />
              <span className="mobile-tab-label">{config.label}</span>
            </button>
          );
        })}

        {hasMore && (
          <button
            className={`mobile-tab ${activeTab === "more" ? "active" : ""}`}
            onClick={() => setActiveTab(prev => prev === "more" ? null : "more")}
          >
            <LayoutGrid size={22} />
            <span className="mobile-tab-label">Más</span>
          </button>
        )}

        <button
          className={`mobile-tab ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab(prev => prev === "profile" ? null : "profile")}
        >
          {(() => {
            const totalBadgeCount =
              (showTicketsIndicator ? (openTicketsCount || 0) : 0) +
              (showIncidencesIndicator ? (openIncidencesCount || 0) : 0);
            return totalBadgeCount > 0 && (
              <span className="mobile-tab-badge">{totalBadgeCount}</span>
            );
          })()}
          <UserIcon size={22} />
          <span className="mobile-tab-label">Perfil</span>
        </button>
      </div>

      {/* Mobile Menus */}
      {favoriteTabs.map((tab) => {
        const config = menuSections[tab];
        const Icon = config.icon;
        return (
          <MobileMenuModal key={tab} open={activeTab === tab} onClose={() => setActiveTab(null)} title={config.label} icon={Icon}>
            <div className="mobile-menu-section">
              {tab === 'puntos' && (
                <>
                  <div className="mobile-menu-section-title">Canales</div>
                  {canAccess(role, type, category, 'machines') && (
                    <NavLinkItem to="/machines" icon={Monitor}>ONEs</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'minis') && (
                    <NavLinkItem to="/minis" icon={Boxes}>MINIs</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'operativa' && (
                <>
                  <div className="mobile-menu-section-title">Operativa</div>
                  {(canAccess(role, type, category, 'customers') || canAccess(role, type, category, 'contracts')) && <div className="mobile-menu-sublabel">Clientes</div>}
                  {canAccess(role, type, category, 'customers') && (
                    <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'contracts') && (
                    <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
                  )}
                  {(canAccess(role, type, category, 'audit') || canAccess(role, type, category, 'tpvs')) && <div className="mobile-menu-sublabel">Control</div>}
                  {canAccess(role, type, category, 'audit') && (
                    <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'tpvs') && (
                    <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'ventas' && (
                <>
                  <div className="mobile-menu-section-title">Reporte</div>
                  {(canAccess(role, type, category, 'arqueos') || canAccess(role, type, category, 'operational-dashboard')) && <div className="mobile-menu-sublabel">Dashboards</div>}
                  {canAccess(role, type, category, 'arqueos') && (
                    <>
                      <NavLinkItem to="/pro-dashboard" icon={TrendingUp}>Dashboard</NavLinkItem>
                    </>
                  )}
                  {canAccess(role, type, category, 'operational-dashboard') && (
                    <NavLinkItem to="/operational-dashboard" icon={Gauge}>Dashboard Operativo</NavLinkItem>
                  )}
                  {(canAccess(role, type, category, 'sales') || canAccess(role, type, category, 'objectives') || canAccess(role, type, category, 'comissions')) && <div className="mobile-menu-sublabel">Ventas</div>}
                  {canAccess(role, type, category, 'sales') && (
                    <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'objectives') && (
                    <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'comissions') && (
                    <NavLinkItem to="/comissions" icon={Calculator}>Comisiones</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'fichajes' && (
                <>
                  <div className="mobile-menu-section-title">Fichajes</div>
                  {canAccess(role, type, category, 'shifts') && (
                    <NavLinkItem to="/shifts" icon={Clock}>Jornada Promotor</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'soporte' && (
                <>
                  <div className="mobile-menu-section-title">Soporte</div>
                  {(canAccess(role, type, category, 'supportticketadd') || canAccess(role, type, category, 'supportchat')) && <div className="mobile-menu-sublabel">Contacto</div>}
                  {canAccess(role, type, category, 'supportticketadd') && (
                    <NavLinkItem to="/supportticketadd" icon={Info}>Mis Tickets</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'supportchat') && (
                    <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
                  )}
                  {(canAccess(role, type, category, 'faqs') || canAccess(role, type, category, 'manuals')) && <div className="mobile-menu-sublabel">Documentación</div>}
                  {canAccess(role, type, category, 'faqs') && (
                    <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'manuals') && (
                    <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'sat' && (
                <>
                  <div className="mobile-menu-section-title">SAT</div>
                  {(canAccess(role, type, category, 'supporttickets') || canAccess(role, type, category, 'incidences') || canAccess(role, type, category, 'slas')) && <div className="mobile-menu-sublabel">Incidencias</div>}
                  {canAccess(role, type, category, 'supporttickets') && (
                    <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'incidences') && (
                    <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'slas') && (
                    <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'bowler') && <div className="mobile-menu-sublabel">Seguimiento</div>}
                  {canAccess(role, type, category, 'bowler') && (
                    <NavLinkItem to="/bowler" icon={BarChart2}>Bowler</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'admin' && (
                <>
                  <div className="mobile-menu-section-title">Administración</div>
                  {(canAccess(role, type, category, 'customers') || canAccess(role, type, category, 'contracts')) && <div className="mobile-menu-sublabel">Clientes</div>}
                  {canAccess(role, type, category, 'customers') && (
                    <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'contracts') && (
                    <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
                  )}
                  {(canAccess(role, type, category, 'promoters') || canAccess(role, type, category, 'users')) && <div className="mobile-menu-sublabel">Personas</div>}
                  {canAccess(role, type, category, 'promoters') && (
                    <NavLinkItem to="/promoters" icon={Users}>Promotores</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'users') && (
                    <NavLinkItem to="/users" icon={UserCog}>Trabajadores</NavLinkItem>
                  )}
                  {type === 'MASTER' && <div className="mobile-menu-sublabel">Datos</div>}
                  {type === 'MASTER' && (
                    <NavLinkItem to="/sync" icon={RefreshCw}>Base de Datos Sync</NavLinkItem>
                  )}
                  {type === 'MASTER' && (
                    <NavLinkItem to="/customer-ids" icon={IdCard}>Clientes · ID</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'training' && (
                <>
                  <div className="mobile-menu-section-title">Training</div>
                  <NavLinkItem to={`/training/${userId}`} icon={BookOpen}>Cursos</NavLinkItem>
                  {hasAdmin && (
                    <NavLinkItem to="/training-admin" icon={NotebookTabs}>Gestión de Cursos</NavLinkItem>
                  )}
                </>
              )}
            </div>
          </MobileMenuModal>
        );
      })}

      {/* More Menu */}
      {hasMore && (
        <MobileMenuModal open={activeTab === "more"} onClose={() => setActiveTab(null)} title="Más" icon={LayoutGrid}>
          {moreTabs.map((tab) => {
            const config = menuSections[tab];
            return (
              <div key={tab} className="mobile-menu-section">
                {tab === 'puntos' && hasPuntosVenta && (
                  <>
                    <div className="mobile-menu-section-title">Canales</div>
                    {canAccess(role, type, category, 'machines') && (
                      <NavLinkItem to="/machines" icon={Monitor}>ONEs</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'minis') && (
                      <NavLinkItem to="/minis" icon={Boxes}>MINIs</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'operativa' && hasOperativa && (
                  <>
                    <div className="mobile-menu-section-title">Operativa</div>
                    {(canAccess(role, type, category, 'customers') || canAccess(role, type, category, 'contracts')) && <div className="mobile-menu-sublabel">Clientes</div>}
                    {canAccess(role, type, category, 'customers') && (
                      <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'contracts') && (
                      <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
                    )}
                    {(canAccess(role, type, category, 'audit') || canAccess(role, type, category, 'tpvs')) && <div className="mobile-menu-sublabel">Control</div>}
                    {canAccess(role, type, category, 'audit') && (
                      <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'tpvs') && (
                      <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'ventas' && hasVentas && (
                  <>
                    <div className="mobile-menu-section-title">Reporte</div>
                    {(canAccess(role, type, category, 'operational-dashboard') || canAccess(role, type, category, 'arqueos')) && <div className="mobile-menu-sublabel">Dashboards</div>}
                    {canAccess(role, type, category, 'operational-dashboard') && (
                      <NavLinkItem to="/operational-dashboard" icon={Gauge}>Dashboard Operativo</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'arqueos') && (
                      <>
                        <NavLinkItem to="/arqueos" icon={BarChart2}>Gráficos</NavLinkItem>
                        <NavLinkItem to="/arqueossales" icon={PieChart}>KPI</NavLinkItem>
                      </>
                    )}
                    {(canAccess(role, type, category, 'sales') || canAccess(role, type, category, 'objectives') || canAccess(role, type, category, 'comissions')) && <div className="mobile-menu-sublabel">Ventas</div>}
                    {canAccess(role, type, category, 'sales') && (
                      <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'objectives') && (
                      <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'comissions') && (
                      <NavLinkItem to="/comissions" icon={Calculator}>Comisiones</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'fichajes' && hasFichajes && (
                  <>
                    <div className="mobile-menu-section-title">Fichajes</div>
                    {canAccess(role, type, category, 'shifts') && (
                      <NavLinkItem to="/shifts" icon={Clock}>Jornada Promotor</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'soporte' && hasSoporte && (
                  <>
                    <div className="mobile-menu-section-title">Soporte</div>
                    {(canAccess(role, type, category, 'supportticketadd') || canAccess(role, type, category, 'supportchat')) && <div className="mobile-menu-sublabel">Contacto</div>}
                    {canAccess(role, type, category, 'supportticketadd') && (
                      <NavLinkItem to="/supportticketadd" icon={Info}>Mis Tickets</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'supportchat') && (
                      <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
                    )}
                    {(canAccess(role, type, category, 'faqs') || canAccess(role, type, category, 'manuals') || canAccess(role, type, category, 'api-docs')) && <div className="mobile-menu-sublabel">Documentación</div>}
                    {canAccess(role, type, category, 'faqs') && (
                      <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'manuals') && (
                      <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'api-docs') && (
                      <NavLinkItem to="/api-docs" icon={Newspaper}>API</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'sat' && hasSAT && (
                  <>
                    <div className="mobile-menu-section-title">SAT</div>
                    {(canAccess(role, type, category, 'supporttickets') || canAccess(role, type, category, 'incidences') || canAccess(role, type, category, 'slas')) && <div className="mobile-menu-sublabel">Incidencias</div>}
                    {canAccess(role, type, category, 'supporttickets') && (
                      <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'incidences') && (
                      <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'slas') && (
                      <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'bowler') && <div className="mobile-menu-sublabel">Seguimiento</div>}
                    {canAccess(role, type, category, 'bowler') && (
                      <NavLinkItem to="/bowler" icon={BarChart2}>Bowler</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'comercial' && hasComercial && (
                  <>
                    <div className="mobile-menu-section-title">Comercial</div>
                    {canAccess(role, type, category, 'proposal-configurator') && (
                      <NavLinkItem to="/proposal-configurator" icon={Briefcase}>Configurador</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'proposals') && (
                      <NavLinkItem to="/proposals" icon={FileSignature}>Propuestas</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'proposal-prices') && (
                      <NavLinkItem to="/proposal-prices" icon={Calculator}>Precios</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'admin' && hasAdmin && (
                  <>
                    <div className="mobile-menu-section-title">Administración</div>
                    {(canAccess(role, type, category, 'promoters') || canAccess(role, type, category, 'users')) && <div className="mobile-menu-sublabel">Personas</div>}
                    {canAccess(role, type, category, 'promoters') && (
                      <NavLinkItem to="/promoters" icon={Users}>Promotores</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'users') && (
                      <NavLinkItem to="/users" icon={UserCog}>Trabajadores</NavLinkItem>
                    )}
                    {type === 'MASTER' && <div className="mobile-menu-sublabel">Datos</div>}
                    {type === 'MASTER' && (
                      <NavLinkItem to="/sync" icon={RefreshCw}>Base de Datos Sync</NavLinkItem>
                    )}
                    {type === 'MASTER' && (
                      <NavLinkItem to="/customer-ids" icon={IdCard}>Clientes · ID</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'training' && hasTraining && (
                  <>
                    <div className="mobile-menu-section-title">Training</div>
                    <NavLinkItem to={`/training/${userId}`} icon={BookOpen}>Cursos</NavLinkItem>
                    {hasAdmin && (
                      <NavLinkItem to="/training-admin" icon={NotebookTabs}>Gestión de Cursos</NavLinkItem>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </MobileMenuModal>
      )}

      {/* Profile Menu */}
      <MobileMenuModal
        open={activeTab === "profile"}
        onClose={() => setActiveTab(null)}
        title="Perfil"
        isProfile={true}
        fullname={fullname}
        role={role}
      >
        <div className="mobile-menu-section">

          <div className="mobile-menu-divider" />

          {(showTicketsIndicator || showIncidencesIndicator) && (
            <>
              <div className="mobile-menu-section-title">Notificaciones</div>

              {showTicketsIndicator && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    push('/supporttickets');
                  }}
                >
                  <Ticket size={20} />
                  <span>Tickets de Soporte</span>
                  {openTicketsCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto', background: '#3b82f6' }}>
                      {openTicketsCount}
                    </span>
                  )}
                </button>
              )}

              {showIncidencesIndicator && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    push('/incidences');
                  }}
                >
                  <AlertTriangle size={20} />
                  <span>Incidencias</span>
                  {openIncidencesCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto', background: '#f97316' }}>
                      {openIncidencesCount}
                    </span>
                  )}
                </button>
              )}

              <div className="mobile-menu-divider" />
            </>
          )}

          <div className="mobile-menu-section-title">Configuración</div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <SlidersHorizontal size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Tema</span>
            </div>
            <DarkModeToggle />
          </div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <LayoutGrid size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Idioma</span>
            </div>
            <div style={{ position: 'relative', zIndex: 20 }}>
              <GoogleTranslate variant="modal" />
            </div>
          </div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Coins size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Moneda</span>
            </div>
            <div style={{ position: 'relative', zIndex: 20 }}>
              <CurrencySelector />
            </div>
          </div>

          <div className="mobile-menu-divider" />

          <button className="mobile-menu-item" onClick={onLogout}>
            <LogOut size={20} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </MobileMenuModal>
    </>
  );
};

/**
 * Main AppShell Component
 */
const AppShellBase = ({
  children,
  fullname,
  userId,
  role,
  type,
  category,
  authLogout,
  push,
  fetchOpenTicketsCount,
  fetchOpenIncidencesCount,
  openTicketsCount,
  openIncidencesCount,
  currencyCode,
}) => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  // Only a click-pinned submenu shifts the content; a hover-opened one overlays it.
  const [menuPinned, setMenuPinned] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState(null);
  const [showTools, setShowTools] = useState(false);

  useEffect(() => { injectStyles(); }, []);

  // Command Palette keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Viewport watcher
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, []);

  // Fetch open ticket/incidence counts for indicator badges
  useEffect(() => {
    if (role !== 'ADMIN') return;
    if ((type === 'MASTER' || type === 'SAT1') && fetchOpenTicketsCount) fetchOpenTicketsCount();
    if ((type === 'MASTER' || type === 'SAT2') && fetchOpenIncidencesCount) fetchOpenIncidencesCount();
  }, [role, type, fetchOpenTicketsCount, fetchOpenIncidencesCount]);

  const onNavigate = () => {
    if (isMobile) {
      setMobileOpen(false);
      setMobileActiveTab(null);
    }
  };

  const onLogout = () => {
    authLogout();
    push('/login');
  };

  const goToUser = (userId) => {
    // if (role === 'ADMIN') {
    //   push(`/user/${userId}`);
    // }
  };

  const handleToggleSidebarMobile = () => {
    setMobileOpen(prev => !prev);
  };

  const toggleTools = () => setShowTools(prev => !prev);

  const topFunction = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowTools(false);
  };

  const bottomFunction = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const refreshFunction = () => {
    window.location.reload();
  };

  return (
    <ToastProvider>
      <div className="shell">
        <TopBar
          fullname={fullname}
          userId={userId}
          onLogout={onLogout}
          isMobile={isMobile}
          role={role}
          type={type}
          category={category}
          openTicketsCount={openTicketsCount}
          openIncidencesCount={openIncidencesCount}
          push={push}
          onToggleSidebarMobile={handleToggleSidebarMobile}
          onOpenCommandPalette={() => setCommandPaletteOpen(true)}
          onGoToUser={goToUser}
        />

        <CommandPalette
          isOpen={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          role={role}
          type={type}
          category={category}
          canAccess={canAccess}
          hasAccessToAny={hasAccessToAny}
          push={push}
        />

        {!isMobile && (
          <div className="sidebar-wrapper">
            <Sidebar
              role={role}
              type={type}
              category={category}
              userId={userId}
              onNavigate={onNavigate}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              setMenuPinned={setMenuPinned}
            />
          </div>
        )}

        <main className={`content ${!isMobile ? (activeMenu && menuPinned ? 'shifted' : 'not-shifted') : ''}`}>
          {/* Breadcrumbs */}
          <Breadcrumbs category={category} />

          {/* key: al cambiar la divisa se remonta la vista para que todos los importes se pinten con el símbolo nuevo */}

          <React.Fragment key={currencyCode}>{children}</React.Fragment>
        </main>

        {isMobile && (
          <MobileBottomNav
            role={role}
            type={type}
            category={category}
            onNavigate={onNavigate}
            activeTab={mobileActiveTab}
            setActiveTab={setMobileActiveTab}
            fullname={fullname}
            userId={userId}
            onLogout={onLogout}
            onGoToUser={goToUser}
            openTicketsCount={openTicketsCount}
            openIncidencesCount={openIncidencesCount}
            push={push}
          />
        )}
      </div>
    </ToastProvider>
  );
};

const mapStateToProps = (state) => ({
  fullname: state.auth.fullname,
  userId: state.auth.id,
  role: state.auth.role,
  type: state.auth.type,
  category: state.auth.category,
  currencyCode: state.currency.code,
  openTicketsCount: state.supporttickets.openTicketsCount,
  openIncidencesCount: state.incidences.openIncidencesCount,
});

const mapDispatchToProps = (dispatch) => ({
  authLogout: bindActionCreators(authLogout, dispatch),
  push: bindActionCreators(push, dispatch),
  fetchOpenTicketsCount: bindActionCreators(fetchOpenTicketsCount, dispatch),
  fetchOpenIncidencesCount: bindActionCreators(fetchOpenIncidencesCount, dispatch),
});

export const AppShell = connect(mapStateToProps, mapDispatchToProps)(AppShellBase);
